// Firebase Firestore service functions for the Team Time Tracker
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  setDoc,
  query, 
  orderBy, 
  onSnapshot,
  serverTimestamp,
  where,
  limit
} from 'firebase/firestore';
import { db } from './firebase';

// Collections
const TEAM_MEMBERS_COLLECTION = 'teamMembers';
const TIME_ENTRIES_COLLECTION = 'timeEntries';
const ORGANIZATION_COLLECTION = 'organizations';
const USERS_COLLECTION = 'users';

/**
 * Database Schema:
 * 
 * Organizations:
 * - id: auto-generated
 * - name: string
 * - createdAt: timestamp
 * - updatedAt: timestamp
 * 
 * Team Members:
 * - id: auto-generated
 * - organizationId: string (reference to organization)
 * - name: string
 * - color: string (gradient class)
 * - isActive: boolean
 * - createdAt: timestamp
 * - updatedAt: timestamp
 * 
 * Time Entries:
 * - id: auto-generated
 * - organizationId: string
 * - teamMemberId: string (reference to team member)
 * - date: string (YYYY-MM-DD format)
 * - startTime: timestamp
 * - endTime: timestamp
 * - duration: number (milliseconds)
 * - description: string (optional)
 * - createdAt: timestamp
 * - updatedAt: timestamp
 */

// Helper function to generate random invite code
function generateInviteCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Removed ambiguous characters
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

// Organization Management
export const organizationService = {
  // Create a new organization
  async createOrganization(name, createdBy) {
    try {
      const inviteCode = generateInviteCode();
      const orgData = {
        name,
        createdBy,
        inviteCode,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      const docRef = await addDoc(collection(db, ORGANIZATION_COLLECTION), orgData);
      return { id: docRef.id, ...orgData };
    } catch (error) {
      console.error('Error creating organization:', error);
      throw error;
    }
  },

  // Get organization by ID
  async getOrganization(organizationId) {
    try {
      const docRef = doc(db, ORGANIZATION_COLLECTION, organizationId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting organization:', error);
      throw error;
    }
  },

  // Get organization by invite code
  async getOrganizationByInviteCode(inviteCode) {
    try {
      const q = query(
        collection(db, ORGANIZATION_COLLECTION),
        where('inviteCode', '==', inviteCode.toUpperCase()),
        limit(1)
      );
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting organization by invite code:', error);
      throw error;
    }
  },

  // Delete organization and all related data
  async deleteOrganization(organizationId) {
    try {
      // Delete all team members in this organization
      const membersQuery = query(
        collection(db, TEAM_MEMBERS_COLLECTION),
        where('organizationId', '==', organizationId)
      );
      const membersSnapshot = await getDocs(membersQuery);
      const memberDeletePromises = membersSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(memberDeletePromises);

      // Delete all time entries in this organization
      const entriesQuery = query(
        collection(db, TIME_ENTRIES_COLLECTION),
        where('organizationId', '==', organizationId)
      );
      const entriesSnapshot = await getDocs(entriesQuery);
      const entryDeletePromises = entriesSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(entryDeletePromises);

      // Delete the organization itself
      const orgRef = doc(db, ORGANIZATION_COLLECTION, organizationId);
      await deleteDoc(orgRef);

      console.log(`✅ Deleted organization ${organizationId} and all related data`);
    } catch (error) {
      console.error('Error deleting organization:', error);
      throw error;
    }
  }
};

// Team Member Management
export const teamMemberService = {
  // Get all team members for an organization
  async getTeamMembers(organizationId) {
    try {
      const q = query(
        collection(db, TEAM_MEMBERS_COLLECTION),
        where('organizationId', '==', organizationId),
        where('isActive', '==', true),
        orderBy('createdAt', 'asc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting team members:', error);
      throw error;
    }
  },

  // Listen to real-time updates for team members
  onTeamMembersChange(organizationId, callback) {
    const q = query(
      collection(db, TEAM_MEMBERS_COLLECTION),
      where('organizationId', '==', organizationId),
      where('isActive', '==', true),
      orderBy('createdAt', 'asc')
    );
    
    return onSnapshot(q, (querySnapshot) => {
      const teamMembers = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      callback(teamMembers);
    }, (error) => {
      console.error('Error listening to team members:', error);
      callback([]);
    });
  },

  // Add a new team member
  async addTeamMember(organizationId, name, color, userId = null) {
    try {
      const memberData = {
        organizationId,
        name,
        color,
        userId, // Link to user account
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      const docRef = await addDoc(collection(db, TEAM_MEMBERS_COLLECTION), memberData);
      return { id: docRef.id, ...memberData };
    } catch (error) {
      console.error('Error adding team member:', error);
      throw error;
    }
  },

  // Update a team member
  async updateTeamMember(memberId, updates) {
    try {
      const docRef = doc(db, TEAM_MEMBERS_COLLECTION, memberId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error updating team member:', error);
      throw error;
    }
  },

  // Delete a team member (soft delete)
  async deleteTeamMember(memberId) {
    try {
      const docRef = doc(db, TEAM_MEMBERS_COLLECTION, memberId);
      await updateDoc(docRef, {
        isActive: false,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error deleting team member:', error);
      throw error;
    }
  },

  // Start timer - save the start time to Firebase
  async startTimer(memberId) {
    try {
      const docRef = doc(db, TEAM_MEMBERS_COLLECTION, memberId);
      await updateDoc(docRef, {
        activeTimerStart: Date.now(),
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error starting timer:', error);
      throw error;
    }
  },

  // Stop timer - clear the start time from Firebase
  async stopTimer(memberId) {
    try {
      const docRef = doc(db, TEAM_MEMBERS_COLLECTION, memberId);
      await updateDoc(docRef, {
        activeTimerStart: null,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error stopping timer:', error);
      throw error;
    }
  }
};

// Time Entry Management
export const timeEntryService = {
  // Get time entries for a specific team member and date range
  async getTimeEntries(organizationId, teamMemberId, startDate, endDate) {
    try {
      const q = query(
        collection(db, TIME_ENTRIES_COLLECTION),
        where('organizationId', '==', organizationId),
        where('teamMemberId', '==', teamMemberId),
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        orderBy('date', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting time entries:', error);
      throw error;
    }
  },

  // Get time entries for a specific date
  async getTimeEntriesByDate(organizationId, teamMemberId, date) {
    try {
      const q = query(
        collection(db, TIME_ENTRIES_COLLECTION),
        where('organizationId', '==', organizationId),
        where('teamMemberId', '==', teamMemberId),
        where('date', '==', date)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting time entries by date:', error);
      throw error;
    }
  },

  // Listen to real-time updates for time entries
  onTimeEntriesChange(organizationId, teamMemberId, callback) {
    const q = query(
      collection(db, TIME_ENTRIES_COLLECTION),
      where('organizationId', '==', organizationId),
      where('teamMemberId', '==', teamMemberId),
      orderBy('date', 'desc'),
      orderBy('createdAt', 'desc')
    );
    
    return onSnapshot(q, (querySnapshot) => {
      const timeEntries = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      callback(timeEntries);
    }, (error) => {
      console.error('Error listening to time entries:', error);
      callback([]);
    });
  },

  // Add a new time entry
  async addTimeEntry(organizationId, teamMemberId, startTime, endTime, description = '') {
    try {
      const duration = endTime - startTime;
      const date = new Date(endTime).toISOString().split('T')[0];
      
      console.log('🔥 Firebase addTimeEntry:', {
        startTime: new Date(startTime),
        endTime: new Date(endTime),
        calculatedDate: date,
        duration: duration,
        organizationId,
        teamMemberId
      });
      
      const entryData = {
        organizationId,
        teamMemberId,
        date,
        startTime: new Date(startTime),
        endTime: new Date(endTime),
        duration,
        description,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      console.log('🔥 Saving to Firebase:', entryData);
      
      const docRef = await addDoc(collection(db, TIME_ENTRIES_COLLECTION), entryData);
      const result = { id: docRef.id, ...entryData };
      
      console.log('🔥 Firebase save result:', result);
      return result;
    } catch (error) {
      console.error('Error adding time entry:', error);
      throw error;
    }
  },

  // Update a time entry
  async updateTimeEntry(entryId, updates) {
    try {
      const docRef = doc(db, TIME_ENTRIES_COLLECTION, entryId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error updating time entry:', error);
      throw error;
    }
  },

  // Delete a time entry
  async deleteTimeEntry(entryId) {
    try {
      const docRef = doc(db, TIME_ENTRIES_COLLECTION, entryId);
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      console.error('Error deleting time entry:', error);
      throw error;
    }
  },

  // Get aggregated time data for a team member
  async getAggregatedTimeData(organizationId, teamMemberId, dateRange = 'monthly') {
    try {
      const now = new Date();
      let startDate, endDate;
      
      switch (dateRange) {
        case 'daily':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          endDate = new Date(startDate.getTime() + 24 * 60 * 60 * 1000);
          break;
        case 'weekly':
          const dayOfWeek = now.getDay();
          startDate = new Date(now.getTime() - dayOfWeek * 24 * 60 * 60 * 1000);
          endDate = new Date(startDate.getTime() + 7 * 24 * 60 * 60 * 1000);
          break;
        case 'monthly':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          endDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
          break;
        default:
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          endDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      }
      
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];
      
      const entries = await this.getTimeEntries(organizationId, teamMemberId, startDateStr, endDateStr);
      
      const totalDuration = entries.reduce((sum, entry) => sum + entry.duration, 0);
      
      return {
        totalDuration,
        entryCount: entries.length,
        entries,
        dateRange: {
          start: startDateStr,
          end: endDateStr
        }
      };
    } catch (error) {
      console.error('Error getting aggregated time data:', error);
      throw error;
    }
  }
};

// Utility functions
export const utils = {
  // Format time in milliseconds to HH:MM:SS
  formatTime: (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  },

  // Get date string in YYYY-MM-DD format
  getDateString: (date = new Date()) => {
    return date.toISOString().split('T')[0];
  },

  // Generate random color gradient for new team members
  getRandomColor: () => {
    const colors = [
      'from-blue-500 to-blue-600',
      'from-purple-500 to-purple-600',
      'from-green-500 to-green-600',
      'from-orange-500 to-orange-600',
      'from-pink-500 to-pink-600',
      'from-cyan-500 to-cyan-600',
      'from-red-500 to-red-600',
      'from-yellow-500 to-yellow-600',
      'from-indigo-500 to-indigo-600',
      'from-teal-500 to-teal-600'
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  }
};

// User Service - for managing user profiles and roles
export const userService = {
  // Create or update user profile after Google sign-in
  async createOrUpdateUser(userId, userData) {
    try {
      const userRef = doc(db, USERS_COLLECTION, userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        // Update existing user
        await updateDoc(userRef, {
          ...userData,
          lastLogin: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        return { id: userId, ...userDoc.data(), ...userData };
      } else {
        // Create new user with default role as 'member'
        const newUserData = {
          ...userData,
          role: userData.role || 'member', // 'manager' or 'member'
          organizationId: userData.organizationId || null,
          teamMemberId: null, // Will be linked when manager adds them
          createdAt: serverTimestamp(),
          lastLogin: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        await setDoc(userRef, newUserData);
        return { id: userId, ...newUserData };
      }
    } catch (error) {
      console.error('Error creating/updating user:', error);
      throw error;
    }
  },

  // Get user profile
  async getUser(userId) {
    try {
      const userRef = doc(db, USERS_COLLECTION, userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        return { id: userDoc.id, ...userDoc.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting user:', error);
      throw error;
    }
  },

  // Link user to team member (when manager adds them to team)
  async linkUserToTeamMember(userId, teamMemberId, organizationId) {
    try {
      const userRef = doc(db, USERS_COLLECTION, userId);
      await updateDoc(userRef, {
        teamMemberId,
        organizationId,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error linking user to team member:', error);
      throw error;
    }
  },

  // Update user role (manager only)
  async updateUserRole(userId, role) {
    try {
      const userRef = doc(db, USERS_COLLECTION, userId);
      await updateDoc(userRef, {
        role,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error updating user role:', error);
      throw error;
    }
  },

  // Get all users in an organization
  async getUsersByOrganization(organizationId) {
    try {
      const q = query(
        collection(db, USERS_COLLECTION),
        where('organizationId', '==', organizationId)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting users by organization:', error);
      throw error;
    }
  },

  // Get all organizations a user belongs to
  async getUserOrganizations(userId) {
    try {
      // Get all team members where this user is a member
      const q = query(
        collection(db, TEAM_MEMBERS_COLLECTION),
        where('userId', '==', userId),
        where('isActive', '==', true)
      );
      const querySnapshot = await getDocs(q);
      const teamMembers = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Get unique organization IDs
      const orgIds = [...new Set(teamMembers.map(tm => tm.organizationId))];
      
      // Fetch all organizations
      const organizations = [];
      for (const orgId of orgIds) {
        const org = await organizationService.getOrganization(orgId);
        if (org) {
          organizations.push(org);
        }
      }
      
      return organizations;
    } catch (error) {
      console.error('Error getting user organizations:', error);
      throw error;
    }
  }
};

export default {
  organizationService,
  teamMemberService,
  timeEntryService,
  userService,
  utils
};
